import { CheckCircleIcon, FileTextIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import type { CSSProperties } from "react";
import { useState } from "react";

import { templateImageUrl } from "@/client/constants/template-images";

import type { DocumentTemplate, LetterPreviewVariant } from "../data/templates";

type TemplateCardProps = {
  template: DocumentTemplate;
  selected?: boolean;
  onSelect: (template: DocumentTemplate) => void;
};

type LetterPreviewProps = {
  variant: LetterPreviewVariant;
  accentColor: string;
};

const PreviewLine = ({
  width,
  accentColor,
  highlighted = false,
}: {
  width: string;
  accentColor: string;
  highlighted?: boolean;
}) => {
  return (
    <span
      aria-hidden
      className="block h-0.5 rounded-full sm:h-1"
      style={{
        width,
        backgroundColor: highlighted ? accentColor : "#dbe2ef",
      }}
    />
  );
};

const LetterPreview = ({ variant, accentColor }: LetterPreviewProps) => {
  const style = {
    "--letter-accent": accentColor,
  } as CSSProperties;

  const hasSidebar = variant === "sidebar";

  const hasCorporateHeader = variant === "corporate";

  const hasCornerAccent = variant === "accent";

  const isElegant = variant === "elegant";

  return (
    <div style={style} className="relative h-full overflow-hidden bg-white">
      {hasSidebar && (
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 w-[28%]"
          style={{
            backgroundColor: accentColor,
          }}
        />
      )}

      {hasCorporateHeader && (
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-[22%]"
          style={{
            backgroundColor: accentColor,
          }}
        />
      )}

      {hasCornerAccent && (
        <>
          <div
            aria-hidden
            className="absolute left-0 top-0 size-12 -translate-x-6 -translate-y-6 rotate-45 sm:size-16 sm:-translate-x-8 sm:-translate-y-8"
            style={{
              backgroundColor: accentColor,
            }}
          />

          <div
            aria-hidden
            className="absolute right-0 top-0 size-12 -translate-y-6 translate-x-6 rotate-45 opacity-70 sm:size-16 sm:-translate-y-8 sm:translate-x-8"
            style={{
              backgroundColor: accentColor,
            }}
          />
        </>
      )}

      <div
        className={cn(
          "relative h-full p-2.5 sm:p-4",
          hasSidebar && "ml-[28%] p-2 sm:p-3",
          hasCorporateHeader && "pt-[27%]",
        )}
      >
        <div
          className={cn("border-b pb-1.5 sm:pb-2", isElegant && "text-center")}
          style={{
            borderColor: `${accentColor}55`,
          }}
        >
          <div className={cn("flex items-center gap-1.5", isElegant && "justify-center")}>
            <span
              className="grid size-5 place-items-center rounded-full text-[6px] font-bold text-white sm:size-7 sm:text-[8px]"
              style={{
                backgroundColor: accentColor,
              }}
            >
              OJ
            </span>

            <div className="space-y-1">
              <PreviewLine width="48px" accentColor={accentColor} highlighted />

              <PreviewLine width="32px" accentColor={accentColor} />
            </div>
          </div>

          <div className="mt-1.5 flex gap-1">
            <PreviewLine width="34px" accentColor={accentColor} />

            <PreviewLine width="27px" accentColor={accentColor} />

            <PreviewLine width="30px" accentColor={accentColor} />
          </div>
        </div>

        <div className="mt-2 sm:mt-3">
          <div className="mb-2 ml-auto w-14 space-y-1 sm:mb-3 sm:w-20">
            <PreviewLine width="90%" accentColor={accentColor} />

            <PreviewLine width="65%" accentColor={accentColor} />
          </div>

          <div className="mb-2 space-y-1 sm:mb-3">
            <PreviewLine width="42px" accentColor={accentColor} highlighted />

            <PreviewLine width="58px" accentColor={accentColor} />

            <PreviewLine width="48px" accentColor={accentColor} />
          </div>

          <div className="space-y-1 sm:space-y-1.5">
            <PreviewLine width="94%" accentColor={accentColor} />

            <PreviewLine width="100%" accentColor={accentColor} />

            <PreviewLine width="88%" accentColor={accentColor} />

            <PreviewLine width="96%" accentColor={accentColor} />

            <PreviewLine width="79%" accentColor={accentColor} />

            <span className="block h-1 sm:h-2" />

            <PreviewLine width="100%" accentColor={accentColor} />

            <PreviewLine width="93%" accentColor={accentColor} />

            <PreviewLine width="84%" accentColor={accentColor} />
          </div>

          <div
            className="mt-2 h-0.5 w-10 rounded-full sm:mt-4 sm:w-14"
            style={{
              backgroundColor: accentColor,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const TemplateCard = ({ template, selected = false, onSelect }: TemplateCardProps) => {
  const isResume = template.kind === "resume";

  // Vignette allégée, image de référence si elle manque.
  const [thumbnail, setThumbnail] = useState(template.thumbnailUrl);

  return (
    <article
      className={cn(
        "group min-w-0 overflow-hidden rounded-xl",
        "border bg-white",
        "transition-all duration-300",
        "hover:-translate-y-0.5 hover:shadow-lg",
        "sm:rounded-2xl",
        selected
          ? "border-indigo-600 ring-2 ring-indigo-100"
          : "border-slate-200 hover:border-indigo-300",
      )}
    >
      <div className="relative aspect-[4/5] max-h-[260px] overflow-hidden bg-slate-50 sm:aspect-[210/270] sm:max-h-none">
        {isResume && template.thumbnailUrl ? (
          <img
            src={thumbnail}
            alt={`Aperçu du modèle ${template.name}`}
            className="size-full object-cover object-top"
            loading="lazy"
            decoding="async"
            onError={() => {
              if (template.templateId) setThumbnail(templateImageUrl(template.templateId));
            }}
          />
        ) : template.letterVariant ? (
          <LetterPreview
            variant={template.letterVariant}
            accentColor={template.accentColor ?? "#4f46e5"}
          />
        ) : (
          <div className="grid size-full place-items-center text-slate-400">
            <FileTextIcon aria-hidden size={30} weight="duotone" />
          </div>
        )}

        <div
          className={cn(
            "absolute inset-x-0 bottom-0",
            "bg-gradient-to-t from-indigo-800/95",
            "via-indigo-700/75 to-transparent",
            "px-2 pb-2 pt-8",
            "opacity-100 transition-opacity duration-300",
            "sm:px-3 sm:pb-3 sm:pt-12",
            "lg:opacity-0",
            "lg:group-hover:opacity-100",
            "lg:group-focus-within:opacity-100",
          )}
        >
          <button
            type="button"
            onClick={() => {
              onSelect(template);
            }}
            aria-pressed={selected}
            className={cn(
              "flex min-h-8 w-full items-center justify-center gap-1",
              "rounded-lg bg-white px-2",
              "text-[10px] font-bold text-indigo-700",
              "shadow-md transition-colors hover:bg-indigo-50",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-white",
              "sm:min-h-10 sm:gap-1.5 sm:px-3 sm:text-xs",
            )}
          >
            {selected && <CheckCircleIcon aria-hidden size={14} weight="fill" />}

            <span className="truncate">Utiliser ce modèle</span>
          </button>
        </div>

        {selected && (
          <span
            aria-label="Modèle sélectionné"
            className={cn(
              "absolute right-2 top-2 grid size-6",
              "place-items-center rounded-full",
              "bg-indigo-600 text-white shadow-lg",
              "sm:right-3 sm:top-3 sm:size-8",
            )}
          >
            <CheckCircleIcon aria-hidden size={16} weight="fill" />
          </span>
        )}
      </div>

      <div className="flex min-h-9 items-center px-2.5 sm:min-h-11 sm:px-3.5">
        <h3 className="truncate text-[11px] font-semibold text-indigo-950 sm:text-[13px]">
          {template.name}
        </h3>
      </div>
    </article>
  );
};
